import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
function Economics39() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-38", 
        index: "/index-economics",                 
        next: "/economics-40", 
      };
        const titleText ="Unpacking Minimum Wage Laws";

        const questions = [
  {
    text: "1. What is a primary consequence of setting a minimum wage above the productivity level of some workers?",
    options: [
      { value: "A", label: "A. Increase in consumer spending" },
      { value: "B", label: "B. Higher overall employment" },
      {
        value: "C",
        label: "C. Unemployment for less productive workers",
      },
      { value: "D", label: "D. Boost in small business profits" },
    ],
    correct: "C",
  },
  {
    text: "2. According to the lesson, what happens when businesses are forced to increase prices to pay minimum wages?",
    options: [
      { value: "A", label: "A. Consumers buy more of the product" },
      { value: "B", label: "B. Businesses experience higher profits" },
      {
        value: "C",
        label:
          "C. Consumers switch to substitutes or reduce consumption",
      },
      { value: "D", label: "D. Demand remains unaffected" },
    ],
    correct: "C",
  },
  {
    text: "3. Why does relief money for the unemployed create inefficiencies in the labor market?",
    options: [
      { value: "A", label: "A. It increases employer incentives" },
      { value: "B", label: "B. It raises investment in infrastructure" },
      {
        value: "C",
        label:
          "C. It discourages hiring at wages below the relief amount",
      },
      { value: "D", label: "D. It leads to greater productivity" },
    ],
    correct: "C",
  },
  {
    text: "4. What is identified as the true driver of wage growth in the long run?",
    options: [
      { value: "A", label: "A. Government-imposed wage floors" },
      { value: "B", label: "B. Higher tax rates" },
      {
        value: "C",
        label: "C. Collective bargaining alone",
      },
      {
        value: "D",
        label:
          "D. Increased labor productivity through capital, training, and innovation",
      },
    ],
    correct: "D",
  },
];
 // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);
    
  return (
    <div className="container">
  <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={39}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        {" "}
        Welcome to <b>Day 39</b>! Today’s lesson explores the economic
        implications of minimum wage laws. While often introduced with good
        intentions, such policies can lead to unintended consequences like
        unemployment, reduced business viability, and artificial distortions in
        the labor market. We will examine how real wage growth is achieved not
        through mandates, but through productivity, innovation, and capital
        investment. This lesson will help you understand why sustainable
        prosperity comes from value creation, not regulation.
        <br />
        <br />
        <b>Wages Are Prices Too</b>
        <br />
        <br />
        In previous lessons, we have examined how government-imposed price
        controls distort markets and harm the very people they aim to help.
        Whether it's price ceilings on essential goods or rent control in
        housing markets, we found that intervention in pricing leads to
        shortages, misallocation of resources, and long-term inefficiencies. It
        is only logical that the same consequences extend to the labor market,
        because wages, fundamentally, are just prices i.e the price paid for
        labor or service. Yet, strangely, many who argue fervently against price
        controls in goods and housing often turn around and advocate for minimum
        wage laws with the belief that such laws help the working class. This
        contradiction is widespread, yet largely unexamined.
        <br />
        To understand why this is a contradiction, we need to re-emphasize that
        prices whether for bread, housing, or labor emerge from voluntary
        exchanges in the market between buyers and sellers. In the labor market,
        employers are buyers of labor, and workers are the sellers. Just as no
        one is forced to buy a loaf of bread at an overpriced cost, no one
        should be forced to pay a higher wage than what a particular type of
        labor can command in the open market. When governments impose a legal
        minimum wage say $15 per hour, it artificially inflates the price of
        labor beyond what the market might support for certain jobs or skill
        levels. This leads to the inevitable outcome: anyone whose skills cannot
        justify that wage will not be hired. Thus, minimum wage laws create
        unemployment, especially among the young, inexperienced, or less
        educated workers.
        <br />
        Instead of viewing wages as moral obligations or tools of social
        justice, they must be understood as economic signals that reflect
        productivity. A wage is not a reward for effort or need it is a function
        of the value produced by the worker. When wages are artificially
        increased beyond this value, market dynamics will adjust accordingly
        often in ways that are harmful. Automation will accelerate, jobs will be
        outsourced, or businesses will simply shut down. All of these are
        rational responses to a government-mandated wage that does not align
        with the productive capacity of the worker.
        <br />
        <br />
        <b>Who Really Pays for the Minimum Wage?</b>
        <br />
        <br />
        Advocates of the minimum wage often argue that businesses can simply
        “pass on” the increased cost of labor to consumers. In this way, the
        burden supposedly shifts from the employer to society at large. At first
        glance, this may sound plausible. After all, if a fast food chain is
        required to pay its workers more, can’t it just raise the price of
        burgers by a few cents? But this line of reasoning collapses under
        deeper scrutiny.
        <br />
        Prices in a competitive market are not arbitrary. They are the result of
        supply and demand, and any significant deviation from the natural price
        level triggers behavioral change. If the price of a basic good like food
        or clothing rises due to increased labor costs, consumers are not
        passive observers. They will reduce consumption, seek substitutes, or
        shift their loyalty to competitors perhaps in sectors or geographies not
        subject to the same wage mandates. This results in reduced revenues,
        thinning profit margins, and eventually the closure of less profitable
        businesses. What began as a noble attempt to increase wages ends up
        shrinking the very industries that employ large numbers of low-skill
        workers.
        <br />
        The idea that society “as a whole” pays the cost assumes there is no
        real loss involved. But there is. When marginal businesses shut down or
        cut staff, unemployment rises. When products become more expensive, real
        purchasing power falls especially among the poor. And when the tax
        burden increases to fund wage relief or unemployment benefits, it
        reduces the ability of entrepreneurs to reinvest in businesses, which
        could have otherwise created more jobs. In short, the fantasy that the
        burden of wage laws can be invisibly spread across the economy conceals
        the visible damage: lost jobs, reduced opportunities, and an overall
        decline in economic dynamism.
        <br />
        Moreover, once the government begins to intervene in wages, a cascading
        set of compensatory policies becomes necessary. If a worker cannot
        produce value equal to the minimum wage, he will be laid off. Then, to
        prevent public unrest or moral outrage, the state must offer
        unemployment relief. But this relief must come from somewhere either by
        taxing those who are still working or by printing more money, both of
        which harm the economy in other ways. What began as a simple policy to
        “raise wages” snowballs into a system of dependency, distortion, and
        dysfunction.
        <br />
        <br />
        <b>The Trap of Relief</b>
        <br />
        <br />
        Once minimum wage laws push certain workers out of employment, the state
        faces pressure to support them through relief programs. These programs
        may include direct payments, food assistance, subsidized housing, or
        even government-provided jobs. While these schemes appear compassionate
        on the surface, they are riddled with unintended consequences that
        undermine their goals.
        <br />
        Consider a worker who was previously employable at $90 a week, but the
        law mandates a minimum wage of $100. If he is laid off and receives $70
        in unemployment relief, society suffers in two ways. First, the worker
        is deprived of the dignity and routine of employment; second, his
        potential contribution which is valued at $90 is lost entirely. This is
        a double loss: to the individual and to the economy. No work is
        performed, no value is created, and taxpayer money is consumed to
        sustain this idleness. And yet, perversely, this situation is treated as
        a policy success because the worker is no longer “exploited” with a low
        wage.
        <br />
        Even when governments try to replace unemployment relief with “work
        relief” programs, the outcome is not much better. Such programs often
        involve creating jobs that would not exist in a free market, simply to
        justify payment. The worker may appear employed, but in reality, part of
        his wage is a hidden subsidy that fails to reflect the true value of his
        labor. This leads to inefficient allocation of resources, distorted
        market signals, and the moral hazard of expecting pay without productive
        effort.
        <br />
        Work relief also introduces bureaucratic inefficiencies. Government jobs
        are often poorly managed, lacking in accountability, and wasteful in
        execution. Since the jobs are not created by market demand but by
        political decree, they fail to produce real wealth. Eventually, the cost
        of maintaining such programs becomes a burden on the very economy they
        are meant to help. And all of this stems from the original error:
        imposing a wage floor that prices some workers out of the labor market.
        <br />
        By removing the ability of workers and employers to freely negotiate
        wages based on skills, demand, and productivity, we eliminate the most
        effective mechanism for employment: voluntary exchange. Rather than
        lifting people out of poverty, these interventions trap them in a cycle
        of dependency and lost potential. The intentions may be noble, but the
        outcomes are economically and morally corrosive.
        <br />
        <br />
        <b>Productivity, Not Politics</b>
        <br />
        <br />
        Critics of minimum wage opposition often accuse economists of being
        indifferent to the plight of the poor. But this is a strawman. No one
        argues against higher wages per se. In fact, the ultimate goal of any
        sound economic policy should be to raise the standard of living for
        everyone, including low-income workers. The disagreement is about how to
        achieve that goal. And history shows that prosperity comes not through
        legislative fiat, but through rising productivity.
        <br />A society becomes wealthier when its workers become more
        productive. Productivity, in turn, increases through the accumulation of
        capital, innovation, better tools, and improved education. When workers
        are equipped with machines, software, or skills that allow them to
        produce more in less time, their value to employers increases and so do
        their wages. This is not a political assertion but an economic reality.
        Wages are not determined by goodwill or decrees; they are determined by
        the market value of output.
        <br />
        Consider the transformation of agriculture. A century ago, most farmers
        were poor and lived hand-to-mouth. Today, thanks to tractors,
        fertilizers, logistics, and automation, a single farmer can feed
        hundreds. As a result, modern farmers earn more, work fewer hours, and
        live better lives not because of wage laws, but because of tools and
        technology. The same logic applies across sectors from manufacturing to
        services to information work
        <br />
        Therefore, if the objective is to increase wages, the solution lies not
        in coercion but in empowerment. Policies should encourage investment,
        entrepreneurship, and innovation. Governments must create environments
        where businesses can thrive, profits can be reinvested, and workers can
        continuously upgrade their skills. Tax structures should incentivize
        capital formation, and educational systems should focus on relevant,
        high-impact training. Only then will wages rise sustainably, in tandem
        with productivity.
        <br />
        By contrast, trying to legislate higher wages without corresponding
        increases in output is like trying to extract more juice from a dry
        fruit. It leads to disappointment, inefficiency, and ultimately,
        decline. Real wage growth must be earned through value creation—not
        decreed from a government office.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Minimum wage laws are rooted in the same economic fallacy as other forms
        of price control. Though they are often championed in the name of
        justice and fairness, their consequences are far from ideal. By setting
        a wage floor, governments prevent mutually beneficial exchanges between
        employers and workers, leading to increased unemployment, reduced
        business competitiveness, and burdensome welfare programs. True and
        lasting wage growth is not the result of legislation but of increased
        labor productivity achieved through capital accumulation, innovation,
        and skill development. A sustainable and prosperous society is one that
        trusts the free market to allocate wages based on real value, rather
        than political mandates.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In Day 40, we turn our attention to one of the most misunderstood forces
        in economics: inflation. While many believe it simply means rising
        prices, the truth is far more deceptive and dangerous. We will explore
        how inflation quietly erodes purchasing power, why it benefits some at
        the expense of others, and how government control over money creation
        distorts the economy. Prepare to uncover the hidden costs of inflation
        and how it affects your everyday life without you even noticing.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics39;
